import { useCallback } from 'react';
import { prop } from 'ramda';
import { useDispatch } from 'react-redux';

import { dispatcher } from 'dispatcher';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'js/store/hooks';

import { useVerifyMarketingPreferencesDirect }
    from '@interlayer/js/app/components/popups/ConfirmMarketingPreferencesPopup/hooks/useVerifyMarketingPreferences';
import { useGetURLTemplates } from 'js/hooks/useGetURLTemplates';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { pushDispatcher } from '@core/js/hooks/socket/hooks/usePushSubscription';
import { useVerifyEmailChange } from 'js/app/hooks/user/hooks/useVerifyEmailChange';
import { useAllPlayerTags } from '@interlayer/js/app/hooks/user/hooks/useAllPlayerTags';
import { useGetUrlTemplatesForSegmentation } from 'js/app/hooks/user/hooks/useGetUrlTemplatesForSegmentation';
import {
    useFetchBalance,
    useFetchUserDetails,
    useGetPaymentsConfiguration,
    useGetPaymentStatistics
} from 'js/hooks/user';

const mergeResults = async (results) => results.reduce((acc, v) => {
    if (v.status === 'fulfilled') {
        return { ...acc, ...v.value };
    }

    return acc;
}, {});

export const useAfterLoginAction = () => {
    const verifyJumio = useVerifyJumio();
    const getUrlTemplates = useGetURLTemplates();
    const fetchUserDetails = useFetchUserDetails();
    const getPaymentsConfiguration = useGetPaymentsConfiguration();
    const fetchBalance = useFetchBalance();
    const verifyEmailChange = useVerifyEmailChange();
    const verifyMarketingPreferences = useVerifyMarketingPreferencesDirect();
    const getPaymentStatistics = useGetPaymentStatistics();
    const getUrlTemplatesForSegmentation = useGetUrlTemplatesForSegmentation();
    const { fetch: fetchAllPlayerTags } = useAllPlayerTags({ isForceFetch: true });

    return useCallback(() => Promise.all([
        Promise.allSettled([
            fetchAllPlayerTags({ isUpdate: true })
                .then(async tags => ({ playerTags: tags })),
            fetchUserDetails()
                .then(verifyEmailChange)
                .then(verifyJumio)
        ])
            .then(mergeResults)
            .then(verifyMarketingPreferences),
        getUrlTemplates(),
        getUrlTemplatesForSegmentation(),
        getPaymentStatistics(),
        getPaymentsConfiguration(),
        fetchBalance()
    ]), [
        fetchAllPlayerTags,
        fetchUserDetails,
        verifyJumio,
        getUrlTemplates,
        getUrlTemplatesForSegmentation,
        fetchBalance,
        getPaymentStatistics,
        getPaymentsConfiguration,
        verifyEmailChange,
        verifyMarketingPreferences
    ]);
};

/* core, added return object(84 line) */
export const useVerifyJumio = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const netVerifyJumioEnabled = useAppSelector(prop('netVerifyJumioEnabled'));

    return useCallback((args) => {
        const { suspended, ageVerificationStatus } = args;
        const params = new URLSearchParams(location.search);
        const isHideKYC = params.get('hideKYC') === '1';
        const isUserSuspended = suspended === 'true';
        const isJumioAllowed = netVerifyJumioEnabled && ageVerificationStatus === 'inprocess';

        if (isJumioAllowed && !isHideKYC) {
            dispatch(userSetAttributesAction({ suspended: isUserSuspended }));

            pushDispatcher.trigger('userVerify', isUserSuspended);

            if (isUserSuspended) {
                dispatcher.dispatch('auth:suspend', true);

                return Promise.reject(false);
            }

            dispatcher.dispatch('auth:suspend', false);
        }

        return { isSkipVerificaton: true, userData: args };
    }, [location, netVerifyJumioEnabled, dispatch]);
};
